import React, { useState } from 'react';
import api from '../../services/api';
import * as adminService from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';

interface UserEditModalProps {
  user: {
    id: string;
    username: string;
    email: string;
    full_name?: string;
    disabled: boolean;
    is_superuser: boolean;
    created_at: string;
    updated_at: string;
    auth_provider?: string;
  };
  onClose: () => void;
  onUserUpdated: () => void;
  darkMode: boolean;
}

export const UserEditModal: React.FC<UserEditModalProps> = ({ 
  user, 
  onClose, 
  onUserUpdated,
  darkMode 
}) => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    disabled: user.disabled,
    full_name: user.full_name || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.put(`/auth/users/${user.id}`, {
        disabled: formData.disabled,
        full_name: formData.full_name || undefined,
      });
      
      showToast('User updated successfully', 'success');

      onUserUpdated();
      onClose();
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to update user', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`relative w-full max-w-md p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Edit User
        </h2>
        
        <div className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm font-medium">Username:</p>
              <p className="text-base">{user.username}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Email:</p>
              <p className="text-base">{user.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Auth Provider:</p>
              <p className="text-base">{user.auth_provider || 'local'}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Role:</p>
              <select
                value={user.is_superuser ? 'admin' : 'viewer'}
                onChange={async (e) => {
                  try {
                    await adminService.assignRole(user.id, e.target.value);
                    showToast('Role updated', 'success');
                    onUserUpdated();
                    onClose();
                  } catch (err: any) {
                    showToast(err.response?.data?.detail || 'Failed to update role', 'error');
                  }
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div>
              <p className="text-sm font-medium">Created:</p>
              <p className="text-base">{new Date(user.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Last Updated:</p>
              <p className="text-base">{new Date(user.updated_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Full Name
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
          
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="disabled"
                checked={formData.disabled}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className={`ml-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Disable User
              </span>
            </label>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-md ${
                darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md ${
                darkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
